import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function ContactForm() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    const data = new URLSearchParams(new FormData(formRef.current) as unknown as Record<string, string>);
    setStatus('loading');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setStatus('success'); formRef.current.reset(); setCharCount(0); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  const inputClass = `w-full h-12 border border-sage/20 rounded-md px-4 text-sm font-inter text-charcoal bg-canvas outline-none focus:border-sage focus:ring-1 focus:ring-sage/30 transition-all duration-200 placeholder:text-text-muted/50`;

  if (status === 'success') {
    return (
      <div className="bg-canvas border border-sage/20 rounded-2xl p-12 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-sage/15 mx-auto mb-5">
          <i className="ri-check-line text-2xl text-sage" />
        </div>
        <h3 className="font-cormorant text-3xl font-light text-charcoal mb-3">{t('contact.form.successTitle')}</h3>
        <p className="font-inter text-sm text-text-muted leading-relaxed max-w-xs mx-auto mb-8">
          {t('contact.form.successDesc')}
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="whitespace-nowrap px-6 py-2.5 rounded-md border border-sage/20 text-sm font-inter text-charcoal hover:border-sage hover:text-sage transition-all duration-200 cursor-pointer"
        >
          {t('contact.form.successBtn')}
        </button>
      </div>
    );
  }

  return (
    <form ref={formRef} data-readdy-form onSubmit={handleSubmit} className="bg-canvas border border-sage/20 rounded-2xl p-8 md:p-12">
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-inter font-medium text-charcoal mb-1.5" htmlFor="name">
              {t('contact.form.name')} <span className="text-sage">*</span>
            </label>
            <input id="name" name="name" type="text" required placeholder={t('contact.form.namePlaceholder')} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-inter font-medium text-charcoal mb-1.5" htmlFor="email">
              {t('contact.form.email')} <span className="text-sage">*</span>
            </label>
            <input id="email" name="email" type="email" required placeholder={t('contact.form.emailPlaceholder')} className={inputClass} />
          </div>
        </div>

        <div>
          <label className="block text-xs font-inter font-medium text-charcoal mb-1.5" htmlFor="phone">
            {t('contact.form.phone')} <span className="text-text-muted font-normal">{t('contact.form.phoneOptional')}</span>
          </label>
          <input id="phone" name="phone" type="tel" placeholder={t('contact.form.phonePlaceholder')} className={inputClass} />
        </div>

        <div>
          <label className="block text-xs font-inter font-medium text-charcoal mb-1.5" htmlFor="service">
            {t('contact.form.service')}
          </label>
          <select id="service" name="service" className={`${inputClass} cursor-pointer`}>
            <option value="">{t('contact.form.serviceDefault')}</option>
            <option>{t('contact.form.service1')}</option>
            <option>{t('contact.form.service2')}</option>
            <option>{t('contact.form.service3')}</option>
            <option>{t('contact.form.service4')}</option>
            <option>{t('contact.form.service5')}</option>
            <option>{t('contact.form.service6')}</option>
          </select>
        </div>

        <div>
          <p className="text-xs font-inter font-medium text-charcoal mb-2.5">{t('contact.form.preferred')}</p>
          <div className="flex flex-wrap gap-5">
            {(['contactEmail', 'contactPhone', 'contactEither'] as const).map((key) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="preferred_contact"
                  value={t(`contact.form.${key}`)}
                  defaultChecked={key === 'contactEither'}
                  className="accent-sage cursor-pointer"
                />
                <span className="text-sm font-inter text-text-muted">{t(`contact.form.${key}`)}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-inter font-medium text-charcoal mb-1.5" htmlFor="message">
            {t('contact.form.message')} <span className="text-sage">*</span>
          </label>
          <textarea
            id="message" name="message" required rows={5} maxLength={500}
            placeholder={t('contact.form.messagePlaceholder')}
            onChange={(e) => setCharCount(e.target.value.length)}
            className="w-full border border-sage/20 rounded-md px-4 py-3.5 text-sm font-inter text-charcoal bg-canvas outline-none focus:border-sage focus:ring-1 focus:ring-sage/30 transition-all duration-200 resize-none placeholder:text-text-muted/50"
          />
          <p className="text-xs text-text-muted font-inter text-right mt-1">{charCount}/500</p>
        </div>

        {status === 'error' && (
          <p className="text-xs text-red-500 font-inter -mb-1">{t('contact.form.error')}</p>
        )}
        <button
          type="submit" disabled={status === 'loading'}
          className="whitespace-nowrap w-full h-12 rounded-md bg-beige text-white text-sm font-inter font-medium hover:bg-beige/80 disabled:opacity-60 transition-colors duration-200 cursor-pointer"
        >
          {status === 'loading' ? t('contact.form.sending') : t('contact.form.submit')}
        </button>

        <p className="text-xs text-text-muted font-inter text-center leading-relaxed">
          <i className="ri-lock-line mr-1" />
          {t('contact.form.privacy')}
        </p>
      </div>
    </form>
  );
}
